import type { MouseEvent } from "react";
import { LabLink } from "../chrome";
import {
  LAB_BRAND,
  LAB_CTA,
  LAB_EXERCISE,
  LAB_HEADLINE,
  LAB_LEAD,
  LAB_OTHER,
  LAB_PRICE,
  LAB_SECONDARY,
  LAB_SELLER,
  LAB_TRAINERS,
  LAB_TRUST,
} from "../copy";
import { useFanLesson } from "../lesson";
import "../../styles/lab-lamp.css";

type LabScreen = "landing" | "exercise";

export const LAMP_ID = "lamp" as const;
export const LAMP_NAME = "Лампа";

type Traverse = (href: string, event: MouseEvent<HTMLAnchorElement>) => void;

function Head({
  basePath,
  hrefFor,
  onTraverse,
}: {
  basePath: string;
  hrefFor: (screen: LabScreen) => string;
  onTraverse?: Traverse;
}) {
  return (
    <header className="lamp-head">
      <LabLink className="lamp-brand" href={hrefFor("landing")} onTraverse={onTraverse}>
        {LAB_BRAND}
      </LabLink>
      <LabLink className="lamp-switch" href={basePath} onTraverse={onTraverse}>
        {LAB_OTHER}
      </LabLink>
    </header>
  );
}

export function LampPoster(props: { href: string; onTraverse?: Traverse }) {
  return (
    <LabLink className="lamp-poster" href={props.href} onTraverse={props.onTraverse}>
      <div className="lamp-poster-wide" aria-hidden="true">
        <p className="lamp-poster-emoji">🎧</p>
        <p className="lamp-poster-brand">{LAB_BRAND}</p>
        <p className="lamp-poster-h">
          Три тренажёра —
          <br />
          один живой английский
        </p>
        <span className="lamp-poster-cta">{LAB_CTA}</span>
      </div>
      <div className="lamp-poster-slim" aria-hidden="true">
        <p className="lamp-poster-emoji">🎧</p>
        <div>
          <p className="lamp-poster-brand">{LAB_BRAND}</p>
          <p className="lamp-poster-h">
            Три тренажёра —
            <br />
            один живой английский
          </p>
          <span className="lamp-poster-cta">{LAB_CTA}</span>
        </div>
      </div>
      <span className="sr-only">
        {LAMP_NAME}. {LAB_HEADLINE}
      </span>
    </LabLink>
  );
}

export function LampWorld(props: {
  screen: LabScreen;
  basePath: string;
  hrefFor: (screen: LabScreen) => string;
  onTraverse?: Traverse;
}) {
  return (
    <div data-world={LAMP_ID} data-lab-screen={props.screen} className="lamp-world">
      <Head basePath={props.basePath} hrefFor={props.hrefFor} onTraverse={props.onTraverse} />
      {props.screen === "exercise" ? (
        <LampExercise />
      ) : (
        <LampLanding hrefFor={props.hrefFor} onTraverse={props.onTraverse} />
      )}
    </div>
  );
}

function LampLanding({
  hrefFor,
  onTraverse,
}: {
  hrefFor: (screen: LabScreen) => string;
  onTraverse?: Traverse;
}) {
  return (
    <main className="lamp-landing">
      <p className="lamp-hero-emoji" aria-hidden="true">
        🎧
      </p>
      <h1>
        Три тренажёра —
        <br />
        один живой английский
      </h1>
      <p className="lamp-lead">{LAB_LEAD}</p>
      <div className="lamp-actions">
        <LabLink className="lamp-cta" href={hrefFor("exercise")} onTraverse={onTraverse}>
          {LAB_CTA}
        </LabLink>
        <LabLink className="lamp-text" href={hrefFor("exercise")} onTraverse={onTraverse}>
          {LAB_SECONDARY}
        </LabLink>
      </div>
      <ul className="lamp-chips">
        {LAB_TRAINERS.map((trainer) => (
          <li key={trainer.title}>
            <LabLink href={hrefFor("exercise")} onTraverse={onTraverse}>
              <span aria-hidden="true">{trainer.emoji}</span>
              <strong>{trainer.title}</strong>
              <em>{trainer.goal}</em>
            </LabLink>
          </li>
        ))}
      </ul>
      <p className="lamp-trust">{LAB_TRUST}</p>
      <p className="lamp-meta">
        {LAB_SELLER.name}
        <br />
        {LAB_PRICE}
      </p>
    </main>
  );
}

function LampExercise() {
  const lesson = useFanLesson();
  return (
    <main className="lamp-lesson" data-play={lesson.play}>
      <p className="lamp-kicker">{LAB_EXERCISE.kicker}</p>
      <h1>{LAB_EXERCISE.title}</h1>
      <p className="lamp-lead">{LAB_EXERCISE.lead}</p>
      <div className="lamp-word">
        <span aria-hidden="true">💨</span>
        <strong>{LAB_EXERCISE.word}</strong>
        <em>{LAB_EXERCISE.gloss}</em>
        <p>{LAB_EXERCISE.example}</p>
      </div>
      <p className="lamp-status" role="status">
        {LAB_EXERCISE.status[lesson.play]}
      </p>
      <div className="lamp-tools">
        <button type="button" className={lesson.play === "listen" ? "lamp-cta" : "lamp-ghost"} onClick={lesson.listen}>
          {LAB_EXERCISE.listen}
        </button>
        <button type="button" className={lesson.play === "speak" ? "lamp-cta" : "lamp-ghost"} onClick={lesson.speak}>
          {LAB_EXERCISE.speak}
        </button>
        <button
          type="button"
          className={lesson.play === "done" ? "lamp-cta" : "lamp-ghost"}
          disabled={!lesson.ready}
          onClick={lesson.finish}
        >
          {LAB_EXERCISE.done}
        </button>
      </div>
    </main>
  );
}
